<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import type { OverlayStackSlot } from '#ui-kit/lib/useOverlayStack'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { reactiveOmit } from '@vueuse/core'
import { DialogClose, DialogContent, DialogPortal, injectDialogRootContext, useForwardPropsEmits } from 'reka-ui'
import { computed, onUnmounted, shallowRef, watch } from 'vue'
import { OVERLAY_Z_BASE, OVERLAY_Z_PANEL_OFFSET, useOverlayStack } from '#ui-kit/lib/useOverlayStack'
import { cn } from '#ui-kit/lib/utils'
import { OverlayBackdrop } from '#ui-kit/ui/overlay'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<DialogContentProps & { class?: HTMLAttributes['class']; hideClose?: boolean }>()
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'hideClose')

const forwarded = useForwardPropsEmits(delegatedProps, emits)

// Overlay z-index stack — acquire a slot when the dialog opens, release on
// close/unmount. Nested overlays auto-bump above their parent panel.
const rootContext = injectDialogRootContext()
const { acquire } = useOverlayStack()
const slot = shallowRef<OverlayStackSlot | null>(null)

watch(
  () => rootContext.open.value,
  (isOpen) => {
    if (isOpen) {
      if (!slot.value) {
        slot.value = acquire()
      }
    } else if (slot.value) {
      slot.value.release()
      slot.value = null
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (slot.value) {
    slot.value.release()
    slot.value = null
  }
})

const backdropZ = computed(() => slot.value?.backdropZ.value ?? OVERLAY_Z_BASE)
const panelZ = computed(() => slot.value?.panelZ.value ?? OVERLAY_Z_BASE + OVERLAY_Z_PANEL_OFFSET)
</script>

<template>
  <DialogPortal>
    <OverlayBackdrop data-ui-dialog-backdrop :z-index="backdropZ" />
    <DialogContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'fixed left-1/2 top-1/2 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 duration-200 sm:rounded-lg',
          props.class,
        )
      "
      :style="{ zIndex: panelZ }"
    >
      <slot />

      <DialogClose
        v-if="!hideClose"
        class="absolute right-3 top-2 flex h-8 w-8 items-center justify-center rounded-xs text-muted-fg transition-colors hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-100 disabled:pointer-events-none [&_svg]:size-5"
      >
        <XMarkIcon />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
